/**
 * NewRelic observability provider for production environments.
 * 
 * This provider sends events to NewRelic using the browser agent's addPageAction API.
 * Events are batched and sent to NewRelic servers for analysis and querying.
 * 
 * IMPORTANT: New Relic's addPageAction API stores events in the PageAction table,
 * and the event name parameter becomes the 'actionName' attribute (not 'eventName').
 * When querying in NRQL, use: WHERE actionName = 'auth_signin_success'
 */

import { isClientSide } from 'utils/host-config';
import { ObservabilityProvider } from './types';

/**
 * NewRelic browser agent interface.
 * 
 * The NewRelic browser agent is injected via server-side rendering
 * and exposes a global `window.newrelic` object.
 */
interface NewRelicBrowserAgent {
  addPageAction: (eventName: string, attributes?: Record<string, any>) => void;
  setApplicationVersion?: (version: string) => void;
}

declare global {
  interface Window {
    newrelic?: NewRelicBrowserAgent;
  }
}

export class NewRelicObservabilityProvider implements ObservabilityProvider {
  trackEvent(eventName: string, attributes: Record<string, unknown>): void {
    if (!this.isAvailable()) {
      console.error('[Observability] NewRelic is not available. Event not tracked:', eventName);
      return;
    }

    try {
      window.newrelic!.addPageAction(eventName, attributes);
    } catch (error) {
      console.error('[Observability] Failed to track event to NewRelic:', eventName, error);
    }
  }

  isAvailable(): boolean {
    return (
      isClientSide() &&
      typeof window.newrelic !== 'undefined' &&
      typeof window.newrelic.addPageAction === 'function'
    );
  }
}

